/**
 * Thermal Printer Service using WebUSB API
 * Supports ESC/POS thermal printers (58mm, 80mm)
 */

// ESC/POS Commands
const ESC = '\x1B';
const GS = '\x1D';

export interface PrinterDevice {
  vendorId: number;
  productId: number;
  productName?: string;
}

export interface ReceiptData {
  invoiceNumber: string;
  date: string;
  cashier: string;
  customer?: {
    name: string;
    phone: string;
    tier: string;
  };
  items: Array<{
    name: string;
    quantity: number;
    price: number;
    subtotal: number;
  }>;
  subtotal: number;
  discount?: number;
  markup?: number;
  total: number;
  paymentMethod: string;
  paid?: number;
  change?: number;
  dueDate?: string;
  branchName: string;
  paperWidth?: number; // 58 or 80mm
  continuousMode?: boolean; // If true, no auto-cut (continuous roll)
  headerText?: string; // Custom header from backend
  footerText?: string; // Custom footer from backend
  logoBase64?: string; // Logo image as base64
}

export class ThermalPrinterService {
  private device: USBDevice | null = null;
  private endpoint: USBEndpoint | null = null;

  /**
   * Request printer access from user
   */
  async requestPrinter(): Promise<void> {
    try {
      // Request USB device access
      this.device = await navigator.usb.requestDevice({
        filters: [
          // Common thermal printer vendors
          { vendorId: 0x0416 }, // Bixolon
          { vendorId: 0x04b8 }, // Epson
          { vendorId: 0x154f }, // Goodcom
          { vendorId: 0x0dd4 }, // Posiflex
          { vendorId: 0x0519 }, // Star Micronics
          { vendorId: 0x0fe6 }, // ICS Advent
          { vendorId: 0x20d1 }, // Rongta
        ],
      });

      await this.device.open();
      await this.device.selectConfiguration(1);
      await this.device.claimInterface(0);

      // Find endpoint
      const iface = this.device.configuration?.interfaces[0];
      const alt = iface?.alternates[0];
      this.endpoint = alt?.endpoints.find((ep) => ep.direction === 'out') || null;

      console.log('✅ Printer connected:', this.device.productName);
    } catch (error: any) {
      console.error('❌ Printer connection failed:', error);
      
      if (error.name === 'NotFoundError') {
        throw new Error('Tidak ada printer dipilih. Silakan pilih printer USB dari daftar.');
      } else if (error.message?.includes('No device selected')) {
        throw new Error('Printer tidak dipilih. Silakan coba lagi dan pilih printer dari daftar.');
      } else {
        throw new Error('Gagal terhubung ke printer USB: ' + error.message);
      }
    }
  }

  /**
   * Auto-connect to previously authorized printer
   */
  async autoConnect(): Promise<boolean> {
    try {
      const devices = await navigator.usb.getDevices();
      if (devices.length === 0) return false;

      this.device = devices[0];
      await this.device.open();
      await this.device.selectConfiguration(1);
      await this.device.claimInterface(0);

      const iface = this.device.configuration?.interfaces[0];
      const alt = iface?.alternates[0];
      this.endpoint = alt?.endpoints.find((ep) => ep.direction === 'out') || null;

      console.log('✅ Auto-connected to printer:', this.device.productName);
      return true;
    } catch (error) {
      console.warn('Auto-connect failed:', error);
      return false;
    }
  }

  /**
   * Send raw data to printer
   */
  private async send(data: string): Promise<void> {
    if (!this.device || !this.endpoint) {
      throw new Error('Printer not connected');
    }

    const encoder = new TextEncoder();
    const bytes = encoder.encode(data);

    await this.device.transferOut(this.endpoint.endpointNumber, bytes);
  }

  /**
   * Print receipt with ESC/POS commands
   */
  async printReceipt(data: ReceiptData): Promise<void> {
    let receipt = '';

    // Detect paper width (default 80mm)
    const paperWidth = data.paperWidth || 80;
    const maxChars = paperWidth === 58 ? 32 : 48; // Characters per line
    const separator = '='.repeat(maxChars);
    const itemNameMaxLen = paperWidth === 58 ? 28 : 44; // Item name max length

    // Initialize printer
    receipt += ESC + '@'; // Reset printer
    
    // Font selection: 58mm uses Font B (smaller for narrow paper), 80mm uses Font A (normal)
    if (paperWidth === 58) {
      receipt += ESC + 'M' + '\x01'; // Font B (9x17 small) for 58mm narrow paper
    } else {
      receipt += ESC + 'M' + '\x00'; // Font A (12x24 normal) for 80mm wide paper
    }

    // Header - Center align
    receipt += ESC + 'a' + '\x01'; // Center align
    
    // Use custom header from backend or fallback to default
    const headerLines = (data.headerText || `${data.branchName}\nSparepart & Accessories`).split('\n');
    for (let i = 0; i < headerLines.length; i++) {
      if (i === 0) {
        receipt += ESC + '!' + '\x30'; // Double height & width for first line
        receipt += headerLines[i] + '\n';
        receipt += ESC + '!' + '\x00'; // Normal size
      } else {
        receipt += headerLines[i] + '\n';
      }
    }
    receipt += separator + '\n';

    // Transaction info - Left align
    receipt += ESC + 'a' + '\x00'; // Left align
    
    if (paperWidth === 58) {
      // Compact format for 58mm
      receipt += `${data.invoiceNumber}\n`;
      receipt += `${data.date}\n`;
      receipt += `${data.cashier}\n`;
    } else {
      // Full format for 80mm
      receipt += `No: ${data.invoiceNumber}\n`;
      receipt += `Tanggal: ${data.date}\n`;
      receipt += `Kasir: ${data.cashier}\n`;
    }

    // Customer info
    if (data.customer) {
      if (paperWidth === 58) {
        receipt += `${data.customer.name}\n`;
        receipt += `${data.customer.phone}\n`;
        receipt += `${data.customer.tier}\n`;
      } else {
        receipt += `Customer: ${data.customer.name}\n`;
        receipt += `HP: ${data.customer.phone}\n`;
        receipt += `Tier: ${data.customer.tier}\n`;
      }
    } else {
      if (paperWidth === 58) {
        receipt += `Pelanggan\n`;
      } else {
        receipt += `Customer: Pelanggan\n`;
      }
    }

    receipt += separator + '\n';

    // Items
    for (const item of data.items) {
      const itemName = (item.name || 'Unknown Item').length > itemNameMaxLen 
        ? (item.name || 'Unknown Item').substring(0, itemNameMaxLen - 3) + '...' 
        : (item.name || 'Unknown Item');
      receipt += `${itemName}\n`;
      
      if (paperWidth === 58) {
        // Compact format: "1 x 108.700 = 108.700"
        receipt += `${item.quantity} x ${item.price.toLocaleString('id-ID')}\n`;
        receipt += `= ${item.subtotal.toLocaleString('id-ID')}\n`;
      } else {
        // Full format with spacing
        receipt += `  ${item.quantity} x ${item.price.toLocaleString('id-ID')}`;
        receipt += ` = ${item.subtotal.toLocaleString('id-ID')}\n`;
      }
    }

    receipt += separator + '\n';

    // Totals - Right align
    receipt += ESC + 'a' + '\x02'; // Right align
    
    if (paperWidth === 58) {
      // Compact labels for 58mm
      receipt += `Sub: Rp ${data.subtotal.toLocaleString('id-ID')}\n`;
      
      if (data.markup && data.markup > 0) {
        const markupPct = ((data.markup / data.subtotal) * 100).toFixed(0);
        receipt += `+${markupPct}%: Rp ${data.markup.toLocaleString('id-ID')}\n`;
      }
      
      if (data.discount && data.discount > 0) {
        const discountPct = ((data.discount / data.subtotal) * 100).toFixed(0);
        receipt += `-${discountPct}%: Rp ${data.discount.toLocaleString('id-ID')}\n`;
      }
    } else {
      // Full labels for 80mm
      receipt += `Subtotal: Rp ${data.subtotal.toLocaleString('id-ID')}\n`;
      
      if (data.markup && data.markup > 0) {
        const markupPct = ((data.markup / data.subtotal) * 100).toFixed(0);
        receipt += `Markup (+${markupPct}%): Rp ${data.markup.toLocaleString('id-ID')}\n`;
      }
      
      if (data.discount && data.discount > 0) {
        const discountPct = ((data.discount / data.subtotal) * 100).toFixed(0);
        receipt += `Diskon (-${discountPct}%): Rp ${data.discount.toLocaleString('id-ID')}\n`;
      }
    }

    receipt += ESC + '!' + '\x20'; // Double height
    receipt += `TOTAL: Rp ${data.total.toLocaleString('id-ID')}\n`;
    receipt += ESC + '!' + '\x00'; // Normal size

    receipt += ESC + 'a' + '\x00'; // Left align
    receipt += separator + '\n';

    // Payment info
    const paymentLabels: Record<string, string> = {
      cash: 'TUNAI',
      tempo: 'TEMPO',
      qris: 'QRIS',
      transfer: 'TRANSFER',
    };
    
    if (paperWidth === 58) {
      receipt += `${paymentLabels[data.paymentMethod] || data.paymentMethod.toUpperCase()}\n`;
    } else {
      receipt += `Metode: ${paymentLabels[data.paymentMethod] || data.paymentMethod.toUpperCase()}\n`;
    }

    if (data.paymentMethod === 'cash' && data.paid) {
      if (paperWidth === 58) {
        receipt += `Byr: Rp ${data.paid.toLocaleString('id-ID')}\n`;
        receipt += `Kbl: Rp ${(data.change || 0).toLocaleString('id-ID')}\n`;
      } else {
        receipt += `Bayar: Rp ${data.paid.toLocaleString('id-ID')}\n`;
        receipt += `Kembali: Rp ${(data.change || 0).toLocaleString('id-ID')}\n`;
      }
    } else if (data.paymentMethod === 'tempo' && data.dueDate) {
      if (paperWidth === 58) {
        receipt += `Tempo: ${data.dueDate}\n`;
      } else {
        receipt += `Jatuh Tempo: ${data.dueDate}\n`;
      }
      receipt += ESC + '!' + '\x10'; // Bold
      receipt += `BELUM LUNAS\n`;
      receipt += ESC + '!' + '\x00'; // Normal
    }

    receipt += separator + '\n';

    // Footer - Center align
    receipt += ESC + 'a' + '\x01'; // Center align
    
    // Use custom footer from backend or fallback to default
    const defaultFooter = paperWidth === 58 
      ? 'Terima Kasih\nBarang dibeli\ntdk dpt tukar/kembali'
      : 'Terima Kasih\nBarang yang sudah dibeli\ntidak dapat ditukar/dikembalikan';
    const footerLines = (data.footerText || defaultFooter).split('\n');
    for (const line of footerLines) {
      receipt += line + '\n';
    }
    // Spacing
    if (data.continuousMode) {
      // Continuous mode: more spacing but no auto-cut
      receipt += '\n\n\n\n\n\n\n\n'; // 8 line feeds for manual tear
    } else {
      // Auto-cut mode: standard spacing + cut
      receipt += '\n\n\n\n\n'; // 5 line feeds
      receipt += GS + 'V' + '\x00'; // Full cut
      receipt += '\n\n\n'; // Extra feeds after cut
    }

    // Send to printer
    await this.send(receipt);
  }

  /**
   * Test print
   */
  async testPrint(): Promise<void> {
    let test = '';
    test += ESC + '@'; // Reset
    test += ESC + 'a' + '\x01'; // Center
    test += ESC + '!' + '\x30'; // Double size
    test += 'TEST PRINT\n';
    test += ESC + '!' + '\x00'; // Normal
    test += '================================\n';
    test += 'Printer berhasil terhubung!\n';
    test += `${new Date().toLocaleString('id-ID')}\n`;
    test += '\n\n';
    test += GS + 'V' + '\x41' + '\x00'; // Cut

    await this.send(test);
  }

  /**
   * Disconnect printer
   */
  async disconnect(): Promise<void> {
    if (this.device) {
      try {
        await this.device.close();
      } catch (error) {
        console.warn('Error closing device:', error);
      }
      this.device = null;
      this.endpoint = null;
      console.log('✅ Printer disconnected');
    }
  }

  /**
   * Check if printer is connected
   */
  isConnected(): boolean {
    return this.device !== null && this.endpoint !== null;
  }

  /**
   * Get printer info
   */
  getPrinterInfo(): string | null {
    return this.device?.productName || null;
  }
}

// Singleton instance
export const thermalPrinter = new ThermalPrinterService();
